<template>
    <div class="xero-invoices w-100 overflow-hidden">
        <template>
            <!-- filters/start -->
            <el-form
                class="px-3 pt-3"
                size="mini"
                :inline="true"
                :model="form"
            >
                <el-form-item
                    v-if="selectedRows.length"
                >
                    <el-badge
                        type="primary"
                        :value="selectedRows.length"
                        class="bulk-import-btn"
                    >
                        <el-button
                            type="primary"
                            style="border-radius: 2px !important"
                            icon="el-icon-upload"
                            @click="bulkInvoiceImport"
                            :disabled="loading.invoices"
                        >IMPORT
                        </el-button>
                    </el-badge>
                </el-form-item>
                <el-form-item>
                    <el-input
                        v-model="form.searchTerm"
                        style="min-width: 400px"
                        placeholder="Search"
                        clearable
                        @input="inputChange"
                        @keyup.enter.native="getQuery"
                        @clear="getQuery"
                    ></el-input>
                </el-form-item>
                <el-form-item>
                    <el-select
                        v-model="form.dateRangeSelect"
                        clearable
                        placeholder="Date Range"
                        popper-class="report-builder-select-popper"
                        @change="getQuery"
                    >
                        <el-option
                            label="Today"
                            value="Today"
                        ></el-option>
                        <el-option
                            label="Yesterday"
                            value="Yesterday"
                        ></el-option>
                        <el-option
                            label="This Week"
                            value="This Week"
                        ></el-option>
                        <el-option
                            label="This Month"
                            value="This Month"
                        ></el-option>
                        <el-option
                            label="This Quarter"
                            value="This Quarter"
                        ></el-option>
                        <el-option
                            label="This Year"
                            value="This Year"
                        ></el-option>
                        <el-option
                            label="Last Week"
                            value="Last Week"
                        ></el-option>
                        <el-option
                            label="Last Month"
                            value="Last Month"
                        ></el-option>
                        <el-option
                            label="Last Quarter"
                            value="Last Quarter"
                        ></el-option>
                        <el-option
                            label="Last Year"
                            value="Last Year"
                        ></el-option>
                        <el-option
                            label="Last 30 Days"
                            value="Last 30 Days"
                        ></el-option>
                        <el-option
                            label="Custom Range"
                            value="Custom Range"
                        ></el-option>
                    </el-select>
                    <el-date-picker
                        v-show="form.dateRangeSelect == 'Custom Range'"
                        v-model="form.dateRange"
                        name="date-range"
                        format="MMM yyyy"
                        type="daterange"
                        range-separator="-"
                        start-placeholder="Start date"
                        end-placeholder="End date"
                        :picker-options="{firstDayOfWeek: 1}"
                        clearable
                        @change="getQuery"
                    ></el-date-picker>
                </el-form-item>
                <el-form-item>
                    <el-button
                        type="primary"
                        style="border-radius: 2px !important"
                        icon="el-icon-s-operation"
                        @click="getQuery"
                        :loading="loading.invoices"
                    >FILTER
                    </el-button>
                </el-form-item>
                <el-form-item>
                    <el-checkbox
                        v-model="form.stage"
                        true-label="15"
                        @change="getQuery"
                    >Completed Only
                    </el-checkbox>
                </el-form-item>
                <el-form-item v-if="!connection">
                    <el-button
                        type="primary"
                        style="border-radius: 2px !important"
                        @click="generateInvoice"
                        :loading="computeInvoiceLoading"
                    >
                        Generate Invoices
                    </el-button>
                </el-form-item>
            </el-form>
            <!-- filters/end -->

            <!-- table/start -->
            <el-table
                ref="xeroInvoicesTable"
                :data="xero.invoices"
                :row-key="(row) => row.ref"
                :default-sort="{prop: 'settlement.remittanceDate', order: 'descending'}"
                class="b-l-0 expandable"
                header-cell-class-name="py-1 text-uppercase font-11"
                :row-class-name="({row, rowIndex}) => { return 'invoice-row invoice-row-' + row.id + ' invoice-row-index-' + rowIndex}"
                :cell-class-name="formatDue"
                :height="getMaxHeight()"
                border
                @row-click="rowClick"
                @expand-change="expandChange"
                @sort-change="sortChange"
                @current-change="(row) => { currentRowKey = row.ref }"
                @selection-change="(val) => selectedRows = val"
                :expand-row-keys="expandedRows"
                :current-row-key="currentRowKey"
            >
                <template slot="empty" :class="{'pb-0': loading.invoices}">
                    <div v-if="loading.invoices">Loading</div>
                </template>
                <el-table-column
                    class="text-center"
                    type="selection"
                    align="center"
                    class-name="mt-1"
                    width="55"
                    :selectable="rowIsSelectable"
                    reserve-selection
                ></el-table-column>
                <el-table-column type="expand">
                    <template slot="header" slot-scope="props">
                        <el-button
                            type="text"
                            class="p-0 font-14"
                            style="color:#C0C4CC"
                            @click="() => expandedLock = !expandedLock"
                        >
                            <div v-show="expandedLock">
                                <i class="fas fa-lock"></i>
                            </div>
                            <div v-show="!expandedLock">
                                <i class="fas fa-lock-open"></i>
                            </div>
                        </el-button>
                    </template>
                    <template slot-scope="props">
                        <div>
                            <div
                                v-if="props.row.xero && props.row.xero.invoiceErrors && connection"
                                class="invoice-errors-container"
                            >
                                <el-badge :value="props.row.xero.invoiceErrors.length" class="w-100">
                                    <el-alert
                                        v-for="(error, index) in props.row.xero.invoiceErrors"
                                        :key="index"
                                        type="error"
                                        :description="error"
                                        class="shadow-sm"
                                        :class="{'d-none': index > 0}"
                                        show-icon
                                    ></el-alert>
                                </el-badge>
                            </div>
                            <div
                                v-if="props.row.xero && props.row.xero.invoiceWarnings"
                                class="invoice-warnings-container"
                            >
                                <el-alert
                                    v-for="(warning, index) in props.row.xero.invoiceWarnings"
                                    :key="index"
                                    type="warning"
                                    :description="warning"
                                    class="shadow-sm"
                                    show-icon
                                ></el-alert>
                            </div>
                            <el-row type="flex" align="top" :gutter="10" v-if="props.row.asset.type">
                                <el-col :span="9">
                                    <div class="mb-2 font-12 text-uppercase text-muted font-weight-bold">
                                        Asset
                                        <span class="text-info">
                                            {{ props.row.asset.year }} {{
                                                props.row.asset.make
                                            }} {{ props.row.asset.model }}
                                        </span>
                                    </div>
                                </el-col>
                                <el-col v-if="props.row.loan.lender.name" :span="10" class="pl-3">
                                    <div class="mb-2 font-12 text-uppercase text-info font-weight-bold">
                                        Finance
                                    </div>
                                </el-col>
                            </el-row>
                            <el-row type="flex" align="middle" :gutter="10" v-if="props.row.asset.type">
                                <el-col :span="4" class="text-center pr-3">
                                    <el-image
                                        class="mix-blend-mode-multiple"
                                        style="width:100%;height:0px;"
                                        :src="props.row.asset.image"
                                        fit="cover"
                                        @load="imageLoaded"
                                        @error="imageLoaded"
                                    >
                                        <div slot="error" class="image-slot text-center" style="font-size: 160px">
                                            <i class="el-icon-picture-outline el-opacity-transition"
                                               style="opacity: 0.1"></i>
                                        </div>
                                    </el-image>
                                </el-col>
                                <el-col :span="5">
                                    <el-row :gutter="10">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Type</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">{{ props.row.asset.type }}</div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="props.row.asset.class">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Class</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">{{ props.row.asset.class }}</div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="props.row.asset.year">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Year</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">{{ props.row.asset.year }}</div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="props.row.asset.assetCost > 0">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Cost</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatCurrency(null, null, props.row.asset.assetCost) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="props.row.asset.deposit > 0">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Deposit
                                            </div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatCurrency(null, null, props.row.asset.deposit) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="props.row.asset.tradet > 0">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Trade</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatCurrency(null, null, props.row.asset.trade) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="props.row.asset.payout > 0">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Payout</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatCurrency(null, null, props.row.asset.payout) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="props.row.asset.assetValuation > 0">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Valuation
                                            </div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatCurrency(null, null, props.row.asset.assetValuation) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="_.get(props.row, 'asset.seller.type.name')">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Seller
                                                Type
                                            </div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">{{ props.row.asset.seller.type.name }}</div>
                                        </el-col>
                                    </el-row>
                                    <el-row :gutter="10" v-if="_.get(props.row, 'asset.seller.dealership.name')">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Dealership
                                            </div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{
                                                    _.startCase(_.toLower(_.get(props.row, 'asset.seller.dealership.name')))
                                                }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                </el-col>
                                <el-col v-if="props.row.loan.lender.name" class="finance-col-1 pl-3" :span="4">
                                    <el-row>
                                        <el-col :span="24">
                                            <div v-if="props.row.loan.lender.logo" class="mb-2">
                                                <img
                                                    class="mix-blend-mode-multiple"
                                                    style="width:100%;height:auto;padding-right:10%;object-fit:contain"
                                                    :src="props.row.loan.lender.logo"
                                                />
                                            </div>
                                        </el-col>
                                    </el-row>
                                </el-col>
                                <el-col v-if="props.row.loan.lender.name" class="finance-col-2" :span="5">
                                    <el-row>
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Lender</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">{{ props.row.loan.lender.name }}</div>
                                        </el-col>
                                    </el-row>
                                    <el-row v-if="props.row.loan.product.name">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Product
                                            </div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">{{ props.row.loan.product.name }}</div>
                                        </el-col>
                                    </el-row>
                                    <el-row v-if="_.get(props.row, 'loan.type.name')">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Type</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{
                                                    getUnifiedString(_.get(props.row, 'loan.type.name'), _.get(props.row,
                                                        'loan.product.type.name'))
                                                }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row v-if="_.get(props.row, 'loan.package.writingRate')">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Rate</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatPercentage(props.row.loan.package.writingRate) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row v-if="_.get(props.row, 'broker.name')">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Broker</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">{{ props.row.broker.name }}</div>
                                        </el-col>
                                    </el-row>
                                </el-col>
                                <el-col v-if="props.row.loan.lender.name" class="finance-col-3" :span="5" :offset="1">
                                    <el-row v-if="props.row.loan.finance.term">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Term</div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">{{ props.row.loan.finance.term }}</div>
                                        </el-col>
                                    </el-row>
                                    <el-row v-if="props.row.loan.finance.amount">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Financed
                                            </div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatCurrency(null, null, props.row.loan.finance.amount) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row v-if="_.get(props.row, 'loan.submission.contractNumber')">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Contract
                                                No
                                            </div>
                                        </el-col>
                                        <el-col :span="16" class="text-uppercase">
                                            <div class="font-11 text-muted">
                                                {{ props.row.loan.submission.contractNumber }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row v-if="_.get(props.row, 'settlement.remittanceDate')">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Remittance
                                            </div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatDate(null, null, props.row.settlement.remittanceDate) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                    <el-row v-if="_.get(props.row, 'settlement.completedDate')">
                                        <el-col :span="8">
                                            <div class="font-11 text-uppercase text-muted font-weight-bold">Completed
                                            </div>
                                        </el-col>
                                        <el-col :span="16">
                                            <div class="font-11 text-muted">
                                                {{ formatDate(null, null, props.row.settlement.completedDate) }}
                                            </div>
                                        </el-col>
                                    </el-row>
                                </el-col>
                            </el-row>

                            <el-row :gutter="10" class="pt-2">
                                <el-col :span="9">
                                    <div class="mb-2 text-uppercase text-info font-weight-bold font-12">
                                        Commissions
                                    </div>
                                    <el-table
                                        header-cell-class-name="py-2 text-uppercase font-11"
                                        :row-class-name="getInvoiceRowCss"
                                        :data="getInvoiceRecords(props.row)"
                                        border
                                        class="shadow-sm"
                                    >
                                        <el-table-column
                                            label="Type"
                                            prop="commissionType"
                                            width="120"
                                        ></el-table-column>
                                        <el-table-column
                                            label="Provider"
                                            min-width="180"
                                            prop="entity"
                                            show-overflow-tooltip
                                        >
                                            <template slot-scope="scope">
                                                <el-tag
                                                    v-if="scope.row.fintelPays"
                                                    class="font-11 mr-1"
                                                    size="mini"
                                                    type="info"
                                                    effect="dark"
                                                >FINTELLIGENCE
                                                </el-tag>
                                                <span>{{ scope.row.entity }}</span>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            label="Commission"
                                            prop="amount"
                                            width="100"
                                            :formatter="formatCurrency"
                                        ></el-table-column>
                                    </el-table>
                                </el-col>

                                <el-col :span="connection ? 15 : 24" :class="connection ? 'fin-pl-3' : 'fin-pl-0'">
                                    <el-alert v-if="!_.isEmpty(compError)"
                                              :description="compError"
                                              type="error"/>

                                    <div class="fin-flex fin-justify-between fin-items-center fin-mb-2">
                                        <div class="text-uppercase text-info font-weight-bold font-12">
                                            Invoices
                                        </div>
                                        <div v-if="!connection">
                                            <div class="fin-flex fin-space-x-3">
                                                <el-button
                                                    class="fin-rounded fin-px-2 fin-py-2 fin-bg-blue-500 fin-text-white"
                                                    @click="saveManualInvoices(props.row)"
                                                    :loading="saveManualInvoicesLoading"
                                                >
                                                    Save invoices
                                                </el-button>

                                                <el-button
                                                    class="fin-rounded fin-px-2 fin-py-2 fin:hover:bg-blue-600"
                                                    @click="addManualInvoice(props.row)"
                                                >
                                                    <span><i class="fas fa-plus"></i></span> <span
                                                    class="pl-1">Add</span>
                                                </el-button>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="hasUploadableInvoices(props.row) && connection"
                                        class="pt-3"
                                        style="position: absolute; left: 69%; top: calc(50% + 30px); transform: translate(-50%, -50%); z-index: 100;"
                                    >
                                        <el-link
                                            class="font-4xl"
                                            :type="props.row.xero.invoiceErrors ? 'error' : 'primary'"
                                            :icon="props.row.xero.invoiceErrors ? 'el-icon-warning' : 'el-icon-upload'"
                                            :loading="submittingInvoices.includes(props.row.id)"
                                            :disabled="!!props.row.xero.invoiceErrors || submittingInvoices.includes(props.row.id)"
                                            @click="submitInvoices(props.$index, props.row)"
                                        >
                                            <div class="font-14">
                                                Import<span v-if="submittingInvoices.includes(props.row.id)">ing</span>
                                                {{
                                                    _.get(props.row, 'xero.toBeSubmitted', 0) - _.get(props.row,
                                                        'xero.invoiceCount', 0)
                                                }}
                                                Invoice(s)
                                            </div>
                                        </el-link>
                                        <div v-if="submittingInvoices.includes(props.row.id)">
                                            <span data-v-46b20d22="" data-v-644ea9c9="" class="loading-wave-dots"
                                                  style="left:50%; bottom: -10px" spinner="waveDots"><span
                                                class="wave-item"></span><span class="wave-item"></span><span
                                                class="wave-item"></span><span class="wave-item"></span><span
                                                class="wave-item"></span></span>
                                        </div>
                                    </div>

                                    <el-table
                                        ref="xeroAppInvoicesTable"
                                        :class="[{'blur': !_.get(props.row, 'xero.invoiceCount', 0)}, 'xero-invoices-table', 'expandable']"
                                        header-cell-class-name="py-2 text-uppercase font-11"
                                        :row-class-name="getInvoiceRowCss"
                                        @row-click="(row) => _.get(row, 'invoice.payments', []).length || _.get(row, 'invoice.creditNotes', []).length ? $refs.xeroAppInvoicesTable.toggleRowExpansion(row) : false"
                                        :data="getInvoiceRecords(props.row).filter(invoice => !['Referrer', 'Referral', 'Voucher'].includes(invoice.commissionType))"
                                        class="shadow-sm"
                                        border
                                        v-if="connection"
                                    >
                                        <el-table-column v-if="props.row.xero.invoicePaid" type="expand">
                                            <template slot-scope="scope">
                                                <div>
                                                    <el-row v-if="_.get(scope.row, 'invoice.payments', []).length"
                                                            :gutter="10">
                                                        <el-col :span="24">
                                                            <div
                                                                class="mb-2 text-uppercase text-info font-weight-bold font-12">
                                                                Payments
                                                            </div>
                                                            <el-table
                                                                header-cell-class-name="py-2 text-uppercase font-11"
                                                                :data="_.get(scope.row, 'invoice.payments', [])"
                                                                class="shadow-sm"
                                                                border
                                                            >
                                                                <el-table-column
                                                                    label="Payment"
                                                                    class-name="pr-2"
                                                                    show-overflow-tooltip
                                                                >
                                                                    <template slot-scope="scope"
                                                                              v-if="scope.row.paymentId">
                                                                        <el-link
                                                                            type="default"
                                                                            icon="el-icon-link"
                                                                            class="d-inline"
                                                                            :href="scope.row.url"
                                                                            target="_blank"
                                                                        >{{ scope.row.paymentId }}
                                                                        </el-link>
                                                                    </template>
                                                                </el-table-column>
                                                                <el-table-column
                                                                    prop="reference"
                                                                    label="Reference"
                                                                    show-overflow-tooltip
                                                                ></el-table-column>
                                                                <el-table-column
                                                                    prop="amount"
                                                                    label="Paid"
                                                                    width="100"
                                                                    :formatter="formatCurrency"
                                                                ></el-table-column>
                                                                <el-table-column
                                                                    prop="date"
                                                                    label="Date"
                                                                    width="100"
                                                                    :formatter="formatDate"
                                                                ></el-table-column>
                                                            </el-table>
                                                        </el-col>
                                                    </el-row>
                                                    <el-row v-if="_.get(scope.row, 'invoice.creditNotes', []).length"
                                                            :gutter="10">
                                                        <el-col :span="24"
                                                                :class="{'pt-3': _.get(scope.row, 'invoice.payments', []).length}">
                                                            <div
                                                                class="mb-2 text-uppercase text-info font-weight-bold font-12">
                                                                Credit Notes
                                                            </div>
                                                            <el-table
                                                                header-cell-class-name="py-2 text-uppercase font-11"
                                                                :data="_.get(scope.row, 'invoice.creditNotes', [])"
                                                                class="shadow-sm"
                                                                border
                                                            >
                                                                <el-table-column
                                                                    label="Payment"
                                                                    class-name="pr-2"
                                                                    show-overflow-tooltip
                                                                >
                                                                    <template slot-scope="scope"
                                                                              v-if="scope.row.creditNoteId">
                                                                        <el-link
                                                                            type="default"
                                                                            icon="el-icon-link"
                                                                            class="d-inline"
                                                                            :href="scope.row.url"
                                                                            target="_blank"
                                                                        >{{ scope.row.creditNoteId }}
                                                                        </el-link>
                                                                    </template>
                                                                </el-table-column>
                                                                <el-table-column
                                                                    prop="reference"
                                                                    label="Reference"
                                                                    show-overflow-tooltip
                                                                ></el-table-column>
                                                                <el-table-column
                                                                    prop="amount"
                                                                    label="Paid"
                                                                    width="100"
                                                                    :formatter="formatCurrency"
                                                                ></el-table-column>
                                                                <el-table-column
                                                                    prop="date"
                                                                    label="Date"
                                                                    width="100"
                                                                    :formatter="formatDate"
                                                                ></el-table-column>
                                                            </el-table>
                                                        </el-col>
                                                    </el-row>
                                                </div>
                                            </template>
                                        </el-table-column>

                                        <el-table-column
                                            label="Invoice"
                                            class-name="pr-2"
                                            show-overflow-tooltip
                                        >
                                            <template slot-scope="scope" v-if="scope.row.invoice">
                                                <el-link
                                                    :type="getInvoicePaidType(scope.row.invoice)"
                                                    icon="el-icon-link"
                                                    class="d-inline"
                                                    :href="scope.row.invoice.url"
                                                    target="_blank"
                                                >{{ scope.row.invoice.invoiceNumber }}
                                                </el-link>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="invoice.reference"
                                            label="Reference"
                                            show-overflow-tooltip
                                        ></el-table-column>
                                        <el-table-column
                                            prop="invoice.status"
                                            label="Status"
                                            width="100"
                                        ></el-table-column>
                                        <el-table-column
                                            prop="invoice.amountPaid"
                                            label="Paid"
                                            width="100"
                                            :formatter="formatCurrency"
                                        >
                                            <template slot-scope="props" v-if="props.row.invoice">
                                              <span v-if="!props.row.invoice.isPaid">
                                                {{
                                                      formatCurrency(props.row, props.column, props.row.invoice.amountPaid)
                                                  }}
                                              </span>
                                                <el-link v-else
                                                         type="success"
                                                         icon="el-icon-success"
                                                >
                                                    {{
                                                        formatCurrency(props.row, props.column,
                                                            props.row.invoice.amountPaid)
                                                    }}
                                                </el-link>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="invoice.amountDue"
                                            label="Due"
                                            width="100"
                                            :formatter="formatCurrency"
                                        >
                                            <template slot-scope="props" v-if="props.row.invoice">
                                              <span v-if="!props.row.invoice.isUnderpayment">
                                                {{
                                                      formatCurrency(props.row, props.column, props.row.invoice.amountDue)
                                                  }}
                                              </span>
                                                <el-link v-else
                                                         type="warning"
                                                         icon="el-icon-warning"
                                                >
                                                    {{
                                                        formatCurrency(props.row, props.column, props.row.invoice.amountDue)
                                                    }}
                                                </el-link>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="invoice.date"
                                            label="Date"
                                            width="110"
                                            :formatter="formatDate"
                                        ></el-table-column>
                                        <el-table-column
                                            prop="invoice.dueDate"
                                            label="Due Date"
                                            width="110"
                                            :formatter="formatDate"
                                        >
                                            <template slot-scope="props" v-if="props.row.invoice">
                                              <span v-if="!props.row.invoice.isOverdue">
                                                {{ formatDate(props.row, props.column, props.row.invoice.dueDate) }}
                                              </span>
                                                <el-link v-else
                                                         type="error"
                                                         icon="el-icon-warning"
                                                >{{ formatDate(props.row, props.column, props.row.invoice.dueDate) }}
                                                </el-link>
                                            </template>
                                        </el-table-column>
                                    </el-table>

                                    <el-table
                                        ref="xeroAppInvoicesTable"
                                        header-cell-class-name="py-2 text-uppercase font-11"
                                        :row-class-name="getManualInvoiceRowCss"
                                        :data="manualInvoiceData(props.row)"
                                        class="shadow-sm"
                                        border
                                        v-if="!connection"
                                    >
                                        <el-table-column
                                            prop="InvoiceNumber"
                                            label="Invoice"
                                            class-name="pr-2"
                                            show-overflow-tooltip
                                        >
                                            <template slot-scope="props">
                                                <el-input class="fin-w-full" placeholder="Reference"
                                                          v-model="props.row.InvoiceNumber"></el-input>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="Reference"
                                            label="Reference"
                                        >
                                            <template slot-scope="props">
                                                <el-input class="fin-w-full" placeholder="Reference"
                                                          v-model="props.row.Reference"></el-input>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="Status"
                                            label="Status"
                                        >
                                            <template slot-scope="props">
                                                <el-select class="fin-w-full"
                                                           v-model="props.row.Status"
                                                           placeholder="Status" clearable>
                                                    <el-option
                                                        :label="'Paid'"
                                                        :value="'PAID'">
                                                    </el-option>
                                                    <el-option
                                                        :label="'UnPaid'"
                                                        :value="'UNPAID'">
                                                    </el-option>
                                                    <el-option
                                                        :label="'Overdue'"
                                                        :value="'OVERDUE'">
                                                    </el-option>
                                                </el-select>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="AmountPaid"
                                            label="Paid"
                                            :formatter="formatCurrency"
                                        >
                                            <template slot-scope="props">
                                                <div v-if="_.get(props.row, 'Status') !== 'PAID'">
                                                    <application-input
                                                        class="fin-w-full input-belt"
                                                        isCurrency
                                                        v-model.number="props.row.AmountPaid"
                                                        type="number"
                                                    >
                                                    </application-input>
                                                </div>
                                                <el-link v-else
                                                         type="success"
                                                         icon="el-icon-success"
                                                >
                                                    {{
                                                        formatCurrency(props.row, props.column,
                                                            props.row.AmountPaid)
                                                    }}
                                                </el-link>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="AmountDue"
                                            label="Due"
                                            :formatter="formatCurrency"
                                        >
                                            <template slot-scope="props">
                                                <application-input
                                                    class="fin-w-full input-belt"
                                                    isCurrency
                                                    v-model="props.row.AmountDue"
                                                >
                                                </application-input>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="Date"
                                            label="Date"
                                        >
                                            <template slot-scope="props">
                                                <el-date-picker
                                                    class="fin-w-full"
                                                    placeholder="Enter Date"
                                                    type="date"
                                                    v-model="props.row.Date"
                                                    format="yyyy-MM-dd"></el-date-picker>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            prop="DueDate"
                                            label="Due Date"
                                        >
                                            <template slot-scope="props">
                                                <el-date-picker
                                                    class="fin-w-full"
                                                    placeholder="Enter Date"
                                                    type="date"
                                                    v-model="props.row.DueDate"
                                                    format="yyyy-MM-dd"></el-date-picker>
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            label="Action"
                                            width="100"
                                        >
                                            <template slot-scope="props">
                                                <el-button
                                                    v-if="props.$index !== 0"
                                                    class="fin-rounded-2xl fin-px-1 fin-py-1 fin-bg-red-600"
                                                    @click="removeManualInvoice(props.$index, props.row)"
                                                >
                                                    <span><i class="fas fa-times fin-text-white"></i></span>
                                                </el-button>
                                            </template>
                                        </el-table-column>
                                    </el-table>
                                </el-col>
                            </el-row>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column
                    sortable="custom"
                    prop="ref"
                    label="Application"
                    class-name="pr-2"
                    show-overflow-tooltip
                >
                    <template slot-scope="props">
                        <el-link
                            type="default"
                            icon="el-icon-link"
                            class="d-inline"
                            :href="props.row.url"
                            target="_blank"
                        >{{ props.row.ref }}
                        </el-link>
                    </template>
                </el-table-column>
                <el-table-column
                    prop="stage.name"
                    label="Stage"
                ></el-table-column>
                <el-table-column
                    prop="applicants[0].fullName"
                    label="Customer"
                    show-overflow-tooltip
                ></el-table-column>
                <el-table-column
                    prop="broker.name"
                    label="Broker"
                    show-overflow-tooltip
                ></el-table-column>
                <el-table-column
                    prop="loan.lender.name"
                    label="Lender"
                    show-overflow-tooltip
                >
                    <template slot-scope="props">
                          <span v-if="props.row.loan.lender.name">
                            {{ props.row.loan.lender.name }}
                          </span>
                        <span v-else>Insurance Only</span>
                    </template>
                </el-table-column>
                <el-table-column
                    prop="commission.total"
                    label="Invoices"
                    :formatter="formatCurrency"
                ></el-table-column>
                <el-table-column
                    v-if="connection"
                    label="Invoiced"
                >
                    <template slot-scope="props">
                        <span v-if="props.row.xero.invoiceAmount">
                            {{ formatCurrency(props.row, props.column, props.row.xero.invoiceAmount) }}
                         </span>
                        <el-link v-else-if="hasUploadableInvoices(props.row)"
                                 :type="props.row.xero.invoiceErrors ? 'error' : 'primary'"
                                 :icon="submittingInvoices.includes(props.row.id) ? 'el-icon-loading' : (props.row.xero.invoiceErrors ? 'el-icon-warning' : 'el-icon-upload')"
                                 :disabled="!!props.row.xero.invoiceErrors || submittingInvoices.includes(props.row.id)"
                                 @click="submitInvoices(props.$index, props.row)"
                        >Import{{ submittingInvoices.includes(props.row.id) ? 'ing' : '' }}
                        </el-link>
                    </template>
                </el-table-column>
                <el-table-column
                    prop="xero.invoicePaid"
                    label="Paid"
                    :formatter="formatCurrency"
                >
                    <template slot-scope="props">
                        <div v-if="!connection">
                            <el-link
                                v-if="_.get(_.find(isManualPaidTotal, {applicationId: props.row.id}), 'Status', '') == 'PAID'"
                                type="success"
                                icon="el-icon-success"
                            >
                                {{
                                    Intl.NumberFormat('en-US',
                                        {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }
                                    ).format(_.get(_.find(isManualPaidTotal, {applicationId: props.row.id}), 'AmountPaid', 0))
                                }}
                            </el-link>
                            <span v-else>
                                {{
                                    Intl.NumberFormat('en-US',
                                        {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }
                                    ).format(_.get(_.find(isManualPaidTotal, {applicationId: props.row.id}), 'AmountPaid', 0))
                                }}
                            </span>
                        </div>
                        <div v-else>
                            <el-link
                                v-if="_.get(props.row, 'xero.invoiceCount', 0) && props.row.xero.invoicePaid && !props.row.xero.invoiceDue"
                                type="success"
                                icon="el-icon-success"
                            >
                                {{ formatCurrency(props.row, props.column, props.row.xero.invoicePaid) }}
                            </el-link>
                            <span v-else-if="_.get(props.row, 'xero.invoiceCount', 0) && props.row.xero.invoicePaid">
                            {{ formatCurrency(props.row, props.column, props.row.xero.invoicePaid) }}
                            </span>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column
                    prop="xero.invoiceDue"
                    label="Due"
                    :formatter="formatCurrency"
                >
                    <template slot-scope="props">
                        <div v-if="!connection">
                            {{
                                Intl.NumberFormat('en-US',
                                    {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    }
                                ).format(_.get(_.find(isManualDueTotal, {applicationId: props.row.id}), 'AmountDue', 0))
                            }}
                        </div>
                        <div v-else>
                            <span
                                v-if="_.get(props.row, 'xero.invoiceCount', 0) && props.row.xero.invoiceDue && !props.row.xero.invoiceOverdue && !props.row.xero.invoiceUnderpayment">
                            {{ formatCurrency(props.row, props.column, props.row.xero.invoiceDue) }}
                            </span>
                            <el-link
                                v-else-if="_.get(props.row, 'xero.invoiceCount', 0) && props.row.xero.invoiceUnderpayment"
                                type="warning"
                                icon="el-icon-warning"
                            >{{ formatCurrency(props.row, props.column, props.row.xero.invoiceDue) }}
                            </el-link>
                            <el-link v-else-if="props.row.xero.invoiceDue"
                                     type="error"
                                     icon="el-icon-warning"
                            >{{ formatCurrency(props.row, props.column, props.row.xero.invoiceDue) }}
                            </el-link>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column
                    sortable="custom"
                    prop="settlement.remittanceDate"
                    label="Remittance"
                    :formatter="formatDate"
                ></el-table-column>
                <el-table-column
                    sortable="custom"
                    prop="xero.invoiceDate"
                    label="Invoiced On"
                    :formatter="formatDate"
                >
                    <template v-if="_.get(props.row, 'xero.invoiceCount', 0)" slot-scope="props">
                        {{ formatDate(props.row, props.column, _.get(props.row.xero, 'invoiceDate')) }}
                    </template>
                </el-table-column>
                <el-table-column
                    v-if="connection"
                    prop="actions"
                    class="text-center"
                    align="center"
                    class-name="mt-1"
                    width="55"
                >
                    <template slot="header">
                        <el-popover
                            placement="bottom-end"
                            popper-class="p-0"
                            width="220"
                            trigger="click"
                        >
                            <el-menu
                                class="border-0 font-12"
                                active-text-color="#303133"
                            >
                                <el-menu-item
                                    index="1"
                                    @click="scanAndLinkInvoices"
                                >
                                    <i class="el-icon-attract"></i>
                                    <span class="font-12">Scan & Link Invoices</span>
                                </el-menu-item>
                                <el-menu-item index="2">
                                    <i class="el-icon-set-up"></i>
                                    <span class="font-12">Config Columns</span>
                                </el-menu-item>
                                <el-menu-item index="3">
                                    <i class="el-icon-files"></i>
                                    <span class="font-12">View History</span>
                                </el-menu-item>
                            </el-menu>
                            <i slot="reference" class="font-20 el-icon-more"
                               style="color:#C0C4CC;vertical-align:middle;cursor:pointer"></i>
                        </el-popover>
                    </template>
                    <template slot-scope="props">
                        <el-popover
                            placement="bottom-end"
                            popper-class="p-0"
                            width="220"
                            trigger="click"
                        >
                            <el-menu
                                class="border-0 font-12"
                                active-text-color="#303133"
                                @open="true"
                                @close="true"
                            >
                                <el-menu-item index="2">
                                    <i class="el-icon-notebook-2"></i>
                                    <span class="font-12">Add/View Notes</span>
                                </el-menu-item>
                                <el-menu-item index="3">
                                    <i class="el-icon-delete"></i>
                                    <span class="font-12">Void/Delete Invoices</span>
                                </el-menu-item>
                                <el-menu-item index="4">
                                    <i class="el-icon-files"></i>
                                    <span class="font-12">View History</span>
                                </el-menu-item>
                            </el-menu>
                            <i slot="reference" class="font-20 el-icon-more"
                               style="color:#C0C4CC;vertical-align: middle;"></i>
                        </el-popover>
                    </template>
                </el-table-column>
                <infinite-loading
                    slot="append"
                    spinner="waveDots"
                    @infinite="($state) => { loading.invoices = true; getInvoices($state); }"
                    :identifier="infiniteId"
                    force-use-infinite-wrapper=".el-table__body-wrapper"
                >
                    <div class="el-table__empty-block" slot="no-more"><span
                        class="el-table__empty-text">End of results</span></div>
                    <span slot="no-results"></span>
                </infinite-loading>
            </el-table>
            <!-- table/end -->
            <div class="p-3" v-if="total > 0">
                Displaying records <strong>{{ from }}</strong> to <strong>{{ to }}</strong> of
                <strong>{{ total }}</strong>
            </div>
        </template>
    </div>
</template>

<script>
import InfiniteLoading from "vue-infinite-loading";
import ScrollOut from 'scroll-out';

export default {
    name: "XeroInvoices",
    props: {
        connection: [Object, Array]
    },
    data() {
        return {
            infiniteId: +new Date(),
            page: 1,
            perPage: 25,
            from: 0,
            to: 0,
            total: null,
            sort: ['settlement.remittanceDate', 'DESC'],
            form: {
                searchTerm: null,
                stage: null,
                invoiceNumber: null,
                lender: null,
                dateRange: null,
                dateRangeSelect: null,
                dateFrom: null,
                dateTo: null,
            },
            loading: {
                invoices: false,
            },
            submitting: {
                invoices: [],
            },
            xero: {
                invoices: [],
            },
            expandedRows: [],
            selectedRows: [],
            currentRowKey: null,
            expandedLock: true,
            manualInvoices: [],
            allManualInvoices: [],
            saveManualInvoicesLoading: false,
            isManualDueTotal: null,
            isManualPaidTotal: null,
            expandedRow: null,
            computeInvoiceLoading: false,
            compError: ''
        };
    },
    computed: {
        submittingInvoices() {
            return this.submitting.invoices;
        },
    },
    created() {
        Echo.private('xero.' + Ambition.getGlobal('tenant').tenantId + '.invoices').listen(
            ".updated",
            (response) => {
                let summary = response.summary;
                console.log(response)
                let rowIndex = this.xero.invoices.findIndex((invoice) => invoice.ref == summary.ref);

                console.log('invoices', rowIndex)

                this.xero.invoices[rowIndex] = Object.assign(this.xero.invoices[rowIndex], summary);
                this.submitting.invoices = this.submitting.invoices.filter((item) => item != summary.id);
            }
        );
    },
    mounted() {
        this.getAllManualInvoices()
        this.so = ScrollOut({
            scope: this.$el
        })
        this.handleResize();
        window.addEventListener("resize", this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.handleResize);
    },
    watch: {
        'allManualInvoices': {
            handler(val) {
                if (_.size(val) > 0) {
                    this.isManualDueTotal = _(val)
                        .groupBy('applicationId')
                        .map(arr => {
                            const {applicationId, Status, AmountDue} = arr[0];

                            return {
                                applicationId, Status, AmountDue: _.sumBy(arr, function (a) {
                                    return _.toNumber(a.AmountDue)
                                })
                            };
                        })
                        .value();

                    console.log('isManualDueTotal ', this.isManualDueTotal)

                    this.isManualPaidTotal = _(val)
                        .groupBy('applicationId')
                        .map(arr => {
                            const {applicationId, Status} = arr[0];

                            return {
                                applicationId, Status, AmountPaid: _.sumBy(arr, function (a) {
                                    return _.toNumber(a.AmountPaid)
                                })
                            };
                        })
                        .value();
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        scanAndLinkInvoices(menuItem, instance) {
            /** @todo: move the hide to something global for menu, use @select menu event? maybe */
            menuItem.rootMenu.$parent.$refs.popper.style.display = 'none'

            this.$notify({
                iconClass: 'el-icon-attract',
                customClass: 'xero-notification',
                title: 'Scanning & Linking Invoices',
                message: 'Scanning xero for invoices to import into Ambition',
                position: 'bottom-right'
            });

            this.$tenant.get("/admin/accounting/xero/invoices/import")
                .then(({data}) => {
                    this.$notify({
                        type: 'success',
                        customClass: 'xero-notification',
                        title: 'Scanning & Linking Invoices',
                        message: 'This process has completed successfully',
                        position: 'bottom-right'
                    });
                })
                .catch(() => {
                })
                .finally(() => {
                })
        },
        hasUploadableInvoices(row) {
            return row.xero
                && _.get(row, 'xero.toBeSubmitted', 0)
                && _.get(row, 'xero.toBeSubmitted', 0) != _.get(row, 'xero.invoiceCount', 0)
                && row.commission.total;
        },

        handleResize: _.debounce(() => {
            $('.xero-invoices > .el-table').height($('.el-main').height() - 119);
        }, 100),

        output(a1, a2, a3, a4) {
            console.error('output', a1, a2, a3, a4);
        },

        imageLoaded(event) {
            if (event.type == 'error') {
                $('.el-image').css('opacity', 1);
            } else {
                let height = $(event.target).width() / $(event.target).prop('naturalWidth') * $(event.target).prop('naturalHeight')
                $(event.target).css('display', 'none').parent().delay(500).animate({
                    height: height
                }, 500, function () {
                    $(this).children().first().css('display', '').parent().css('opacity', 1);
                })
            }
        },
        rowIsSelectable(row, index) {
            return !!(_.get(row, 'xero.toBeSubmitted', 0) - _.get(row, 'xero.invoiceCount', 0))
                && !_.get(row, 'xero.invoiceErrors')
                && !this.submittingInvoices.includes(row.id);
        },
        getUnifiedString(value1, value2) {
            if (String(value2).includes(value1))
                return value2;
            else if (value1 && value2)
                return value1 + ' - ' + value2;
            else
                return value1 + value2;
        },
        getInvoicePaidType(row) {
            if (_.get(row, 'isPaid'))
                return 'success';

            if (_.get(row, 'isUnderpayment'))
                return 'warning';

            if (_.get(row, 'isOverdue'))
                return 'error';
        },
        getMaxHeight() {
            return ($('.el-main').height() - 115) + 'px';
        },
        getInvoiceRowCss({row}) {
            let invoiceRowCss = '';

            if (!_.get(row, 'invoice.payments', []).length && !_.get(row, 'invoice.creditNotes', []).length)
                invoiceRowCss += 'no-payments';

            if (_.get(row, 'invoice.isOverdue'))
                invoiceRowCss += ' el-alert--error is-light';
            else if (_.get(row, 'invoice.isUnderpayment'))
                invoiceRowCss += ' el-alert--warning is-light';
            else if (_.get(row, 'invoice.isPaid'))
                invoiceRowCss += ' el-alert--success is-light';

            return invoiceRowCss;
        },
        getManualInvoiceRowCss({row}) {
            let invoiceRowCss = '';

            if (_.get(row, 'Status') == 'OVERDUE')
                invoiceRowCss += ' el-alert--error is-light';
            else if (_.get(row, 'Status') == 'UNPAID')
                invoiceRowCss += ' el-alert--warning is-light';
            else if (_.get(row, 'Status') == 'PAID')
                invoiceRowCss += ' el-alert--success is-light';

            return invoiceRowCss
        },
        getAllManualInvoices() {
            this.$tenant.get(`/admin/xero/get-all-manual-invoice`)
                .then((res) => {
                    if (_.get(res, 'data')) {
                        this.allManualInvoices = _.get(res, 'data')
                        console.log('getAllManualInvoices', _.get(res, 'data'))
                    }
                })
                .catch((error) => {
                })
        },
        getManualInvoices(row) {
            this.$tenant.get(`/admin/xero/get-manual-invoice/${_.get(row, 'id')}`)
                .then((res) => {
                    if (_.get(res, 'data')) {
                        this.manualInvoices = _.get(res, 'data')
                        console.log('enableAutomaticallyChangeStatusToOverdueXero ', Ambition.getGlobal('tenant.enableAutomaticallyChangeStatusToOverdueXero'))

                        if (Ambition.getGlobal('tenant.enableAutomaticallyChangeStatusToOverdueXero')) {
                            this.changeManualInvoiceStatusToOverdue(row)
                        }
                    }
                })
                .catch((error) => {
                    console.log('getManualInvoices error ', error)
                })
        },
        saveManualInvoices(row) {
            this.saveManualInvoicesLoading = true

            this.$tenant.post(`/admin/xero/save-manual-invoice/${_.get(row, 'id')}`, {
                manualInvoices: this.manualInvoices
            })
                .then((res) => {
                    this.getManualInvoices(row)
                    this.getAllManualInvoices()
                    this.saveManualInvoicesLoading = false
                })
                .catch((error) => {
                    console.log('saveManualInvoices error ', error)
                    this.saveManualInvoicesLoading = false
                })
        },
        removeManualInvoice(i, v) {
            const index = this.manualInvoices.indexOf(v);

            this.manualInvoices.splice(index, i)

            this.$tenant.post(`/admin/xero/remove-manual-invoice/${_.get(v, 'applicationId')}`, {
                CommissionType: _.get(v, 'CommissionType'),
                InvoiceNumber: _.get(v, 'InvoiceNumber')
            })
                .then((res) => {
                })
                .catch((error) => {
                    console.log('removeManualInvoice error ', error)
                })
        },
        addManualInvoice(row) {
            this.manualInvoices.push({
                applicationId: _.get(row, 'id'),
                appRef: _.get(row, 'ref'),
                Url: _.get(row, 'url'),
                Date: this.$moment().format('YYYY-MM-DD'),
                Type: 'MANUAL',
                Total: '0',
                Status: 'UNPAID',
                DueDate: this.$moment().format('YYYY-MM-DD'),
                InvoiceDate: this.$moment().format('YYYY-MM-DD'),
                AmountCredited: '0',
                InvoiceNumber: '',
                AmountPaid: '0',
                Reference: '',
                XeroID: '',
                AmountDue: '0'
            })
        },
        changeManualInvoiceStatusToOverdue(row) {
            this.$tenant.post(`/admin/xero/change-status-to-overdue/${_.get(row, 'id')}`, {
                manualInvoices: this.manualInvoices
            })
                .then((res) => {
                })
                .catch((error) => {
                })
        },
        manualInvoiceData(row) {
            if (_.size(this.manualInvoices) <= 0) {
                if (_.get(row, 'commission.finance') > 0) {
                    this.manualInvoices.push({
                        applicationId: _.get(row, 'id'),
                        appRef: _.get(row, 'ref'),
                        Url: _.get(row, 'url'),
                        Date: this.$moment().format('YYYY-MM-DD'),
                        Type: 'MANUAL',
                        Total: _.get(row, 'commission.finance'),
                        Status: 'UNPAID',
                        DueDate: this.$moment().format('YYYY-MM-DD'),
                        InvoiceDate: this.$moment().format('YYYY-MM-DD'),
                        AmountCredited: '0',
                        InvoiceNumber: _.get(row, 'ref') + 'FinComm',
                        AmountPaid: '0',
                        Reference: '',
                        XeroID: "manual-" + _.get(row, 'ref') + '-' + 'FinComm',
                        AmountDue: _.get(row, 'commission.finance'),
                        CommissionType: 'FinComm'
                    })
                }

                if (_.get(row, 'commission.dof') > 0) {
                    this.manualInvoices.push({
                        applicationId: _.get(row, 'id'),
                        appRef: _.get(row, 'ref'),
                        Url: _.get(row, 'url'),
                        Date: this.$moment().format('YYYY-MM-DD'),
                        Type: 'MANUAL',
                        Total: _.get(row, 'commission.dof'),
                        Status: 'UNPAID',
                        DueDate: this.$moment().format('YYYY-MM-DD'),
                        InvoiceDate: this.$moment().format('YYYY-MM-DD'),
                        AmountCredited: '0',
                        InvoiceNumber: _.get(row, 'ref') + 'OrigFee',
                        AmountPaid: '0',
                        Reference: '',
                        XeroID: "manual-" + _.get(row, 'ref') + '-' + 'OrigFee',
                        AmountDue: _.get(row, 'commission.dof'),
                        CommissionType: 'OrigFee'
                    })
                }

                _.get(row, 'insurances.items', []).forEach((insurance) => {
                    if (_.get(insurance, 'commission') > 0) {
                        this.manualInvoices.push({
                            applicationId: _.get(row, 'id'),
                            appRef: _.get(row, 'ref'),
                            Url: _.get(row, 'url'),
                            Date: this.$moment().format('YYYY-MM-DD'),
                            Type: 'MANUAL',
                            Total: _.get(insurance, 'commission'),
                            Status: 'UNPAID',
                            DueDate: this.$moment().format('YYYY-MM-DD'),
                            InvoiceDate: this.$moment().format('YYYY-MM-DD'),
                            AmountCredited: '0',
                            InvoiceNumber: _.get(row, 'ref') + _.get(insurance, 'type'),
                            AmountPaid: '0',
                            Reference: '',
                            XeroID: "manual-" + _.get(row, 'ref') + '-' + _.get(insurance, 'type'),
                            AmountDue: _.get(insurance, 'commission'),
                            CommissionType: _.get(insurance, 'type')
                        })
                    }
                })

                if (_.get(row, 'referrerIncoming.amount')) {
                    this.manualInvoices.push({
                        applicationId: _.get(row, 'id'),
                        appRef: _.get(row, 'ref'),
                        Url: _.get(row, 'url'),
                        Date: this.$moment().format('YYYY-MM-DD'),
                        Type: 'MANUAL',
                        Total: _.get(row, 'referrerIncoming.amount'),
                        Status: 'UNPAID',
                        DueDate: this.$moment().format('YYYY-MM-DD'),
                        InvoiceDate: this.$moment().format('YYYY-MM-DD'),
                        AmountCredited: '0',
                        InvoiceNumber: _.get(row, 'ref') + 'Referral',
                        AmountPaid: '0',
                        Reference: '',
                        XeroID: "manual-" + _.get(row, 'ref') + '-' + 'Referral',
                        AmountDue: _.get(row, 'referrerIncoming.amount'),
                        CommissionType: 'Referral'
                    })
                }

                if (_.get(row, 'referrer.id')) {
                    this.manualInvoices.push({
                        applicationId: _.get(row, 'id'),
                        appRef: _.get(row, 'ref'),
                        Url: _.get(row, 'url'),
                        Date: this.$moment().format('YYYY-MM-DD'),
                        Type: 'MANUAL',
                        Total: 0 - _.get(row, 'referrer.fee'),
                        Status: 'UNPAID',
                        DueDate: this.$moment().format('YYYY-MM-DD'),
                        InvoiceDate: this.$moment().format('YYYY-MM-DD'),
                        AmountCredited: '0',
                        InvoiceNumber: _.get(row, 'ref') + 'Referrer',
                        AmountPaid: '0',
                        Reference: '',
                        XeroID: "manual-" + _.get(row, 'ref') + '-' + 'Referrer',
                        AmountDue: _.get(row, 'referrer.fee'),
                        CommissionType: 'Referrer'
                    })
                }

                if (_.get(row, 'referrerVoucher.amount')) {
                    this.manualInvoices.push({
                        applicationId: _.get(row, 'id'),
                        appRef: _.get(row, 'ref'),
                        Url: _.get(row, 'url'),
                        Date: this.$moment().format('YYYY-MM-DD'),
                        Type: 'MANUAL',
                        Total: 0 - _.get(row, 'referrerVoucher.amount'),
                        Status: 'UNPAID',
                        DueDate: this.$moment().format('YYYY-MM-DD'),
                        InvoiceDate: this.$moment().format('YYYY-MM-DD'),
                        AmountCredited: '0',
                        InvoiceNumber: _.get(row, 'ref') + 'Voucher',
                        AmountPaid: '0',
                        Reference: '',
                        XeroID: "manual-" + _.get(row, 'ref') + '-' + 'Voucher',
                        AmountDue: _.get(row, 'referrerVoucher.amount'),
                        CommissionType: 'Voucher'
                    })
                }

                this.expandedRow = row
            }

            return this.manualInvoices
        },
        getInvoiceCellCss({row}) {
            if (_.get(row, 'fintelPays'))
                return 'py-1';
        },

        formatDue({row, column, rowIndex, columnIndex}) {
            // console.error(row, column, rowIndex, colIndex);
            if (column.label == 'Due') {
                if (row.xero.invoiceOverdue > 0) {
                    return 'text-error';
                }
            }

            if (column.label == 'Paid') {
                if (row.xero.invoiceDue == 0 && row.xero.invoicePaid > 0) {
                    // check due dates - maybe add to app object error validation
                    return 'text-success';
                }
            }
        },

        formatPercentage(value) {
            return value + '%';
        },

        formatCurrency(row, column, cellValue, index) {
            return new Intl.NumberFormat('en-US',
                {style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2}
            ).format(cellValue);
        },

        formatDate(row, column, cellValue, index) {
            return cellValue ? this.$moment(cellValue).format("DD/MM/YYYY") : cellValue;
        },

        getInvoiceRecords(row) {
            let records = [];

            if (_.get(row, 'commission.finance') > 0) {
                records.push({
                    entity: _.get(row, 'loan.lender.name'),
                    commissionType: 'FinComm',
                    amount: _.get(row, 'commission.finance'),
                    fintelPays: _.get(row, 'commission.rules.fintelligencePaysFin', false),
                    fintelPayOnClaim: _.get(row, 'commission.rules.payOnClaim', false),
                    fintelSplit: _.get(row, 'commission.rules.commissionSplit', false),
                    invoice: _.get(row, 'xero.invoices', []).find((invoice) => invoice.commissionType == 'FinComm'),
                })
            }

            if (_.get(row, 'commission.dof') > 0) {
                records.push({
                    entity: _.get(row, 'loan.lender.name'),
                    commissionType: 'OrigFee',
                    amount: _.get(row, 'commission.dof'),
                    fintelPays: _.get(row, 'commission.rules.fintelligencePaysDof', false),
                    fintelPayOnClaim: _.get(row, 'commission.rules.payOnClaim', false),
                    fintelSplit: _.get(row, 'commission.rules.commissionSplit', false),
                    invoice: _.get(row, 'xero.invoices', []).find((invoice) => invoice.commissionType == 'OrigFee'),
                })
            }

            _.get(row, 'insurances.items', []).forEach((insurance) => {
                if (insurance.type == 'Comprehensive' && (_.get(insurance, 'frequency') !== "Weekly" || _.get(insurance, 'frequency') !== "Annually")) {
                    this.compError = "Comprehensive Insurance is only generated Annually"
                } else {
                    this.compError = ""
                }

                if (_.get(insurance, 'commission') > 0) {
                    records.push({
                        entity: _.get(insurance, 'name'),
                        commissionType: _.get(insurance, 'type'),
                        amount: _.get(insurance, 'commission'),
                        fintelPays: _.get(insurance, 'rules.fintelligencePays', false),
                        fintelPayOnClaim: _.get(insurance, 'rules.payOnClaim', false),
                        fintelSplit: _.get(row, 'rules.commissionSplit', false),
                        invoice: _.get(row, 'xero.invoices', []).find(
                            (invoice) => {
                                if (insurance.type == 'Comprehensive')
                                    return invoice.commissionType == 'Comp'
                                if (insurance.type == 'GAP')
                                    return invoice.commissionType == 'Gap'
                                if (insurance.type == 'Warranty')
                                    return invoice.commissionType == 'Wty'
                                if (insurance.type == 'CCI/BCI')
                                    return invoice.commissionType == 'CCI'
                            }
                        ),
                    })
                }
            })

            if (_.get(row, 'referrerIncoming.amount')) {
                records.push({
                    entity: _.get(row, 'referrerIncoming.name'),
                    commissionType: 'Referral',
                    amount: _.get(row, 'referrerIncoming.amount')
                })
            }

            if (_.get(row, 'referrer.id')) {
                records.push({
                    entity: _.get(row, 'referrer.name'),
                    commissionType: 'Referrer',
                    amount: 0 - _.get(row, 'referrer.fee')
                })
            }

            if (_.get(row, 'referrerVoucher.amount')) {
                records.push({
                    entity: _.get(row, 'referrerVoucher.name'),
                    commissionType: 'Voucher',
                    amount: 0 - _.get(row, 'referrerVoucher.amount')
                })
            }

            return records;
        },
        invoiceErrors(row) {
            let hasErrors = false;

            hasErrors = _.get(row, 'loan.lender.id', false) && !_.get(row, 'loan.lender.xero.id', false);

            if (hasErrors)
                return true;

            let insurances = _.get(row, 'insurances.items', []).filter(ins => !!_.get(ins, 'commission', false));

            if (!insurances.length)
                return false;

            hasErrors = !!insurances.filter(ins => !_.get(ins, 'xero', false)).length
            return hasErrors;
        },
        generateInvoice() {
            this.computeInvoiceLoading = true

            let params = {
                params: {
                    form: this.form,
                    page: this.page,
                    sort: this.sort
                }
            };

            this.$tenant.get("/admin/xero/generate-manual-invoices", params)
                .then((res) => {
                    console.log('generateInvoice ', res)
                    this.getAllManualInvoices()
                })
                .catch((error) => {
                    console.log('generateInvoice error ', error)
                })
                .finally(() => {
                    this.computeInvoiceLoading = false;
                });
        },
        getQuery(form) {
            this.loading.invoices = true;
            this.page = 1;
            this.xero.invoices = [];
            this.submitting.invoices = [];
            this.$refs.xeroInvoicesTable.clearSelection();
            this.expandedRows = [];
            this.infiniteId += 1;
        },
        inputChange: _.debounce(function (val) {
            this.getQuery();
        }, 800),
        expandChange(row, expanded) {
            if (expanded.includes(row)) {
                if (this.expandedLock) {
                    this.expandedRows = [row.ref];
                } else if (!this.expandedRows.includes(row.ref)) {
                    this.expandedRows.push(row.ref);
                }
            } else if (this.expandedRows.includes(row.ref)) {
                this.expandedRows = this.expandedRows.filter(expanded => expanded != row.ref);
            }

            if (!this.connection) {
                this.getManualInvoices(row)
            }

            this.$nextTick(() => {
                if (this.expandedRows.includes(row.ref)) {
                    $('.xero-invoices > .el-table > .el-table__body-wrapper').delay(100).animate({
                        scrollTop: $('.invoice-row-' + row.id).position().top
                    }, 500);
                }

                this.$nextTick(() => {
                    ScrollOut({
                        targets: '.xero-invoices > .el-table > .el-table__body-wrapper > table > tbody > tr.invoice-row-' + row.id + '.expanded + tr',
                        scrollingElement: '.xero-invoices > .el-table > .el-table__body-wrapper',
                        threshold: function (el, a, b, c) {
                            return (el.previousSibling ? el.previousSibling.offsetHeight : 0) / el.offsetHeight
                        },
                        onShown: function (el) {
                            $(el.previousSibling).addClass('sticky').css('top', -48).animate({top: 0}, 500);
                        },
                        onHidden: function (el) {
                            $(el.previousSibling).removeClass('sticky');
                        }
                    })
                })
            })
        },
        rowClick(row, column, event) {
            if (column && column.property == 'actions') {
                return;
            }
            if (event) {
                event.stopPropagation();
                $(event.target).closest('tr').find('.el-table__expand-column .el-table__expand-icon .el-icon').click();
            }
        },
        sortChange({column, prop, order}) {
            if (!order || order === 'undefined' || !prop || prop === 'undefined') {
                this.sort = ['Date', 'DESC'];
            } else {
                this.sort = [prop, order == 'ascending' ? 'ASC' : 'DESC'];
            }
            this.page = 1;
            this.xero.invoices = [];
            this.infiniteId += 1;
        },
        getInvoices: _.debounce(function ($state) {
            try {

                let params = {
                    params: {
                        form: this.form,
                        page: this.page,
                        sort: this.sort
                    }
                };

                this.loading.invoices = true;

                this.$tenant.get("/admin/accounting/xero/activity", params)
                    .then(({data}) => {
                        if (data && typeof data == 'object' && data.to) {
                            this.xero.invoices = this.xero.invoices.concat(Object.values(data.data));
                            this.from = 1;
                            this.to = data.to;
                            this.total = data.total;
                            if (data.current_page < data.last_page) {
                                $state.loaded();
                                this.page += 1;
                            } else {
                                $state.complete();
                            }
                        } else {
                            $state.complete();
                        }
                    })
                    .catch(() => {
                        $state.complete();
                    })
                    .finally(() => {
                        this.loading.invoices = false;
                    });

            } catch (err) {
                console.error(err);
                $state.complete();
                this.loading.invoices = false;
            }
        }, 500),
        bulkInvoiceImport() {
            let applicationIds = this.selectedRows.map(selectedRow => selectedRow.id);

            let params = {
                params: {
                    applicationIds: applicationIds
                }
            };

            this.submitting.invoices = this.submitting.invoices.concat(applicationIds);
            this.$refs.xeroInvoicesTable.clearSelection();

            this.$tenant.post("/api/xero/invoices", params)
                .catch((data) => {
                    console.log('bulk import error', applicationIds);
                });
        },
        submitInvoices(rowIndex, row) {
            try {
                this.submitting.invoices.push(row.id);

                if (!this.expandedRows.includes(row.ref)) {
                    this.rowClick(row);
                }

                if (_.get(row, 'xero.invoiceErrors', []).length) {
                    this.submitting.invoices = _.remove(this.submitting.invoices, (item) => item == row.id);
                    return;
                }

                this.$tenant.get("/api/xero/invoice/" + row.id)
                    .then((data) => {
                        this.xero.invoices[rowIndex] = Object.assign(this.xero.invoices[rowIndex], data.data);
                    })
                    .finally(() => {
                        this.submitting.invoices = _.remove(this.submitting.invoices, (item) => item == row.id);
                    });
            } catch (err) {
                console.error(err);
                this.submitting.invoices = _.remove(this.submitting.invoices, (item) => item == row.id);
            }
        },
    }
};
</script>

<style lang="scss">
.xero-invoices {

    .font-11 {
        font-size: 11px !important;
    }

    .text-error {
        color: red !important;
    }

    .text-warning {
        color: #E6A23C !important;
    }

    .el-alert--success.is-light {
        color: darkgreen !important;
    }

    /** images */
    .el-image {
        transition: opacity 1s;
        -webkit-transition: opacity 1s;
        opacity: 0;

        &.mix-blend-mode-multiple {
            mix-blend-mode: multiply;
            will-change: opacity;
            height: 100%;
        }
    }

    /** alerts */
    .el-alert {
        .el-alert__content {
            padding: 0;
        }

        .el-alert__description {
            margin: 0px;
            padding-left: 10px;
        }

        .el-icon-close {
            top: calc(50% - 6px);
        }
    }

    /** notifications */
    .el-notification {
        border-radius: 3px;

        .el-notification__title {
            font-size: 16px !important;
        }

        .el-notification__content > p {
            font-size: 12px !important;
        }

        .el-notification__content {
            text-align: left;
        }
    }

    /** links */
    .el-link {

        &.el-link--success {
            color: green !important;

            &:hover {
                color: green !important;

                &.is-underline:after {
                    border-bottom: 1px solid green !important;
                }
            }
        }

        &.el-link--error {
            color: red !important;

            &:hover {
                color: red !important;

                &.is-underline:after {
                    border-bottom: 1px solid red !important;
                }
            }
        }

        &.el-link--warning {
            color: #E6A23C !important;

            &:hover {
                color: #E6A23C !important;
            }
        }

        &.el-link--default {
            color: #52a5b3 !important;

            &:hover {
                color: #52a5b3 !important;

                &.is-underline:after {
                    border-bottom: 1px solid #52a5b3 !important;
                }
            }
        }

        &.el-link--primary {
            &.is-disabled {
                color: #448aff !important;

                &.is-underline:after {
                    border-bottom: 1px solid #448aff !important;
                }
            }
        }

        .el-link--inner {
            font-size: 11px;
        }
    }

    /** checkboxes */
    .el-checkbox {
        margin-bottom: 0;

        .el-checkbox__label {
            font-size: 11px;
        }
    }

    /** buttons */
    .bulk-import-btn {
        margin-top: -1px;

        > sup {
            top: 0.1em;
            font-size: 0.8em;
            height: 1.5em;
            line-height: 1.5em;
            padding: 0 0.4em;
            background: #409eff !important;
            border: none;
            min-width: 17px;
        }
    }

    /** table */
    .el-table {

        font-size: 11px;

        th.is-leaf .cell {
            white-space: nowrap;
        }

        .el-table__row {

            &:hover {
                cursor: pointer;
            }

            &.no-payments .el-table__expand-icon {
                visibility: hidden;
            }

            &.expanded {

                &.sticky {

                    position: sticky;
                    top: 0px;
                    border-top: 1px solid lightgray;
                    -webkit-box-shadow: 0 0.125rem 0.25rem rgb(0 0 0 / 8%) !important;
                    box-shadow: 0 0.125rem 0.25rem rgb(0 0 0 / 8%) !important;
                    z-index: 1000;

                    td {
                        background-color: #fafafa;
                    }

                }

                + tr .el-table__expanded-cell {

                    &[class*=cell] {
                        box-shadow: inset 0 1px 3px #dbdbdb;
                        background: #fafafa !important;
                        padding: 20px 30px;
                    }

                    .el-table .cell {
                        line-height: 20px;
                        min-height: 20px;
                    }

                    .invoice-errors-container > div:last-child,
                    .invoice-warnings-container > div:last-child {
                        margin-bottom: 1rem !important;
                    }

                    .xero-invoices-table {
                        &.blur {
                            opacity: 0.4;
                        }
                    }

                }

            }

        }

    }
}
</style>
